import React, { useEffect, useState } from 'react'
import {
  formatDateOfDifferentFormat,
  timeZone
} from '../../../../utils/DateTimeUtils'
import { useNavigate, useParams } from 'react-router-dom'

import { DDSTypography } from 'den-design-system'
import DisplayCard from '../../../../components/common/Exceptions/DisplayCard'
import GraphDisplayCard from '../../../../components/common/Exceptions/GraphDisplayCard'
import OverviewDoughnutChart from '../../../../components/common/Exceptions/OverviewDoughnutChart'
import { ProjectType } from '../../../../enum/ProjectType.enum'
import Spinner from '../../../../components/common/Spinner/Spinner'
import StockChart from '../../../../components/ExceptionStatistics/StockChart'
import StringConstants from '../../../../constants/StringConstants'
import { getAllQueryParamsAsObjectFromUrl } from '../../../../utils/UrlUtils'
import { getStatisticsOfAnExceptionGroupByGroupId } from '../../../../api/exception/Exception.service'
import { observer } from 'mobx-react-lite'
import pallete from '../../../../global/newPallete'
import { timeFormats } from '../../../../enum/TIME'
import { toPairs } from 'lodash'
import { useStoreContext } from '../../../../store/StoreContext'

interface OverviewProps {
  loadingTag: boolean
  setLoadingTag: (loading: boolean) => void
}

const Overview: React.FC<OverviewProps> = ({ loadingTag, setLoadingTag }) => {
  const store = useStoreContext()
  const navigate = useNavigate()
  const { id } = useParams()
  const [firstReported, setFirstReported] = useState<string>('')
  const [lastReported, setLastReported] = useState<string>('')
  const [maximumReport, setMaximumReport] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [stockChartData, setStockChartData] = useState<any>({})
  const [doughnutChartData, setDoughnutChartData] = useState<any>([])

  const projectType = store.scopeStore.getSelectedProject().type
  const [avgPerDay, setAvgPerDay] = useState<any>()
  const isFullStackProject = projectType === ProjectType.FULL_STACK_ONLY
  const exceptionGroupId = location.pathname.split('/').includes('grouped')
    ? store.exceptionsStoreV2.getCurrentExceptionId()
    : id

  const fetchExceptionStatistics = async () => {
    setLoading(true)
    setLoadingTag(true)

    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)

    try {
      const response: any = await getStatisticsOfAnExceptionGroupByGroupId(
        { $exceptionGroupId: exceptionGroupId as string },
        allQueryParams
      )

      // Updating all local states
      setFirstReported(response?.data?.firstReportedTime)
      setLastReported(response?.data?.latestReportedTime)
      setAvgPerDay(response?.data?.averageReported)
      setMaximumReport(
        Object.values(response?.data?.maximumReportedInADay)[0] as string
      )
      setStockChartData(
        toPairs(response?.data?.responsesByInterval).map(([key, value]) => {
          key = formatDateOfDifferentFormat(key, 'DD/MM/YYYY', 'YYYY-MM-DD')
          return [Number(new Date(key).getTime()), value]
        })
      )

      const messagesLabels = [
        response?.data?.mostOccurredExceptionMessage?.message !== '-'
          ? response.data?.mostOccurredExceptionMessage?.message
          : null,
        response?.data?.secondMostOccurredExceptionMessage.message !== '-'
          ? response.data.secondMostOccurredExceptionMessage.message
          : null,
        'Others'
      ]
      const messagesCount = [
        response?.data?.mostOccurredExceptionMessage?.count,
        response?.data?.secondMostOccurredExceptionMessage?.count > 0
          ? response?.data?.secondMostOccurredExceptionMessage?.count
          : null,
        response?.data?.others?.count
      ]

      setDoughnutChartData({ labels: messagesLabels, counts: messagesCount })

      // Updating store
      store.exceptionsStoreV2.setStatisticsData(response?.data)
      store.exceptionsStoreV2.setStockChartData(
        toPairs(response?.data?.responsesByInterval).map(([key, value]) => {
          key = formatDateOfDifferentFormat(key, 'DD/MM/YYYY', 'YYYY-MM-DD')
          return [Number(new Date(key).getTime()), value]
        })
      )

      store.exceptionStore.setIsRefreshed(false)
    } catch (error) {
      console.error('Error fetching exception statistics:', error)
    } finally {
      setLoading(false)
      setLoadingTag(false)
    }
  }

  useEffect(() => {
    fetchExceptionStatistics()
  }, [
    store.exceptionStore.getIsRefreshed(),
    store.exceptionsStoreV2.getCurrentExceptionId()
  ])

  const statisticsData = store.exceptionsStoreV2.getStatisticsData()
  const responsesByInterval = statisticsData?.responsesByInterval || {}

  return (
    <div className='flex h-[100%] justify-center '>
      {!loading ? (
        <div className='w-full h-full flex flex-col gap-y-[24px] py-[24px]'>
          <div className='grid grid-cols-4 justify-between gap-x-[16px]'>
            <DisplayCard
              id='#'
              heading='First Reported on'
              content={timeZone(
                store.userStore.getOrganizationPreference().timeZone,
                firstReported,
                timeFormats.fullMonthDateYearTime
              )}
            />
            <DisplayCard
              id='#'
              heading='Last Reported on'
              content={timeZone(
                store.userStore.getOrganizationPreference().timeZone,
                lastReported,
                timeFormats.fullMonthDateYearTime
              )}
            />
            <DisplayCard
              id='#'
              heading='Maximum Reported in a day'
              content={loading ? <Spinner /> : maximumReport ?? 0}
            />

            <DisplayCard
              id='#'
              heading='Avg Reported in a day'
              content={loading ? <Spinner /> : avgPerDay ?? 0}
            />
          </div>
          <div className='grid grid-cols-2 justify-between gap-x-[20px] h-[52vh]'>
            <GraphDisplayCard
              heading={isFullStackProject ? 'Exception Trend' : 'Error Trend'}
              content={
                <div className='flex flex-col h-full justify-between align-middle items-center'>
                  <div className='flex flex-col justify-center mt-[100px]'>
                    <StockChart
                      data={store.exceptionsStoreV2.getStockChartData()}
                      startDate={
                        statisticsData?.firstReportedTime
                          ? new Date(statisticsData.firstReportedTime).getTime()
                          : Date.now()
                      }
                      single={Object.keys(responsesByInterval).length === 1}
                      value={
                        Object.keys(responsesByInterval).length === 1
                          ? Object.values(responsesByInterval)[0]
                          : undefined
                      }
                      width={500}
                    />
                  </div>
                  <DDSTypography.Paragraph
                    variant='semiBold'
                    size='para'
                    className='text-center whitespace-nowrap flex justify-center items-center gap-x-[4px] !text-[9.569px]'
                    color={pallete.colors.textDark6}
                  >
                    <span
                      className='h-[1px] w-[16px]'
                      style={{ backgroundColor: pallete.colors.stroke1 }}
                    ></span>
                    Reported Date
                  </DDSTypography.Paragraph>
                </div>
              }
            />

            <GraphDisplayCard
              heading={
                isFullStackProject ? 'Exception Message(s)' : 'Error Message(s)'
              }
              content={
                <OverviewDoughnutChart
                  exceptionGroupId={exceptionGroupId}
                  data={[
                    store.exceptionsStoreV2.getStatisticsData()
                      ?.mostOccurredExceptionMessage?.count,
                    store.exceptionsStoreV2.getStatisticsData()
                      ?.secondMostOccurredExceptionMessage?.count,
                    store.exceptionsStoreV2.getStatisticsData()?.others?.count >
                    0
                      ? store.exceptionsStoreV2.getStatisticsData()?.others
                          ?.count
                      : 0
                  ]}
                  labels={[
                    store.exceptionsStoreV2.getStatisticsData()
                      ?.mostOccurredExceptionMessage?.count > 0
                      ? `${store.exceptionsStoreV2.getStatisticsData()?.mostOccurredExceptionMessage.message} - ${store.exceptionsStoreV2.getStatisticsData()?.mostOccurredExceptionMessage.count}`
                      : '',
                    store.exceptionsStoreV2.getStatisticsData()
                      ?.secondMostOccurredExceptionMessage?.count > 0
                      ? `${store.exceptionsStoreV2.getStatisticsData()?.secondMostOccurredExceptionMessage.message} - ${store.exceptionsStoreV2.getStatisticsData()?.secondMostOccurredExceptionMessage.count}`
                      : '',
                    store.exceptionsStoreV2
                      .getStatisticsData()
                      ?.others?.filter(
                        (other: any) =>
                          other.message !== '-' && other.count !== 0
                      ).length > 0
                      ? StringConstants.OTHERS
                      : ''
                  ]}
                  colors={[
                    pallete.colors.stroke1,
                    pallete.colors.primary1,
                    pallete.colors.warningBlue
                  ]}
                />
              }
            />
          </div>
        </div>
      ) : (
        <Spinner />
      )}
    </div>
  )
}

export default observer(Overview)
